import * as React from "react";
import { useState } from "react";
import { graphql } from "gatsby";
import styled, { css } from "styled-components";
import PageContainer from "../pageContainer";
import { useInView } from "react-intersection-observer";
import { SectionTitle } from "../typography";
import { Table, Tbody, Td, Th, Thead, Tr } from "../table";
import { useLocalization } from "gatsby-theme-i18n";
import theme, { when } from "../../styles/theme";
import ToggleButton from "../toggleButton";

export const fragment = graphql`
  fragment DividendsContent on ContentfulPageSectionDividends {
    title
    dividends {
      year
      interim
      final
      special
      total
    }
    internal {
      type
    }
  }
`;

const translations = {
  en: {
    year: "Year",
    interim: "Interim",
    final: "Final",
    special: "Special",
    total: "Total",
    more: "Show More",
    less: "Show Less",
  },
  zh: {
    year: "年份",
    interim: "中期",
    final: "末期",
    special: "特别息",
    total: "合計",
    more: "展示更多",
    less: "展示更少",
  },
};

const DividendsSection = styled(PageContainer)`
  margin-top: 10rem;
`;

const TableContainer = styled.div`
  margin-top: 3rem;
  margin-left: -${(props) => props.theme.pageContainerWidth.xs}rem;
  margin-right: -${(props) => props.theme.pageContainerWidth.xs}rem;

  ${when("sm")} {
    margin-left: -${(props) => props.theme.pageContainerWidth.sm}rem;
    margin-right: -${(props) => props.theme.pageContainerWidth.sm}rem;
  }

  ${when("md")} {
    margin-left: -${(props) => props.theme.pageContainerWidth.md}rem;
    margin-right: -${(props) => props.theme.pageContainerWidth.md}rem;
  }

  ${when("lg")} {
    margin-left: 0;
    margin-right: 0;
  }
`;

const DividendsTable = styled(Table)``;

const mobileYearCss = css`
  border-top-right-radius: 0.3125rem;
  border-bottom-right-radius: 0.3125rem;
  position: relative;
  margin-bottom: 1rem;

  &:after {
    content: "";
    display: block;
    width: 2rem;
    height: 100%;
    position: absolute;
    left: -2rem;
    top: 0;
    bottom: 0;
    background-color: inherit;
  }

  ${when("md")} {
    display: none;
  }
`;

const desktopYearCss = css`
  border-radius: 0.3125rem;
`;

const Year = styled.div`
  padding: 0.25rem 0.75rem;
  background-color: ${(props) => props.theme.colors.red};
  color: ${(props) => props.theme.colors.white};
  ${(props) => (props.display === "mobile" ? mobileYearCss : desktopYearCss)}
`;

const YearSpacer = styled.div`
  height: 2.9rem;
  margin-bottom: 1rem;

  ${when("md")} {
    display: none;
  }
`;

const TabletOnlyCss = css`
  display: none;

  ${when("md")} {
    display: table-cell;
  }
`;

const TabletOnlyTd = styled(Td)`
  ${TabletOnlyCss}
`;

const TabletOnlyTh = styled(Th)`
  ${TabletOnlyCss}

  &:first-child {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
  }

  ${when("lg")} {
    &:first-child {
      border-bottom-left-radius: 1rem;
      border-top-left-radius: 1rem;
    }
  }
`;

const StyledTh = styled(Th)`
  &:first-child {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
  }

  &:last-child {
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
  }

  ${when("lg")} {
    &:first-child {
      border-bottom-left-radius: 1rem;
      border-top-left-radius: 1rem;
    }

    &:last-child {
      border-bottom-right-radius: 1rem;
      border-top-right-radius: 1rem;
    }
  }
`;

const ButtonContainer = styled.div`
  margin-top: 4rem;
  display: flex;
  justify-content: center;
`;

const Dividends = ({ data, setCurrentSection, index }) => {
  const { ref } = useInView({
    onChange: (inView) => {
      if (inView && setCurrentSection) {
        setCurrentSection(index);
      }
    },
    rootMargin: "-30% 0% -30% 0%",
  });

  const { title, dividends } = data;

  const { locale } = useLocalization();

  const translation = translations[locale];

  const [isActive, setIsActive] = useState();

  return (
    <DividendsSection
      as={"section"}
      hasSideBar={true}
      ref={ref}
      id={`section-${index}`}
    >
      <SectionTitle>{title}</SectionTitle>
      <TableContainer>
        <DividendsTable>
          <Thead background={theme.colors.darkBlue}>
            <Tr>
              <TabletOnlyTh>{translation.year}</TabletOnlyTh>
              <StyledTh>{translation.interim}</StyledTh>
              <StyledTh>{translation.final}</StyledTh>
              <StyledTh>{translation.special}</StyledTh>
              <StyledTh>{translation.total}</StyledTh>
            </Tr>
          </Thead>
          <Tbody>
            {(isActive ? dividends : dividends.slice(0, 5)).map(
              (dividend, index) => {
                return (
                  <Tr key={index}>
                    <TabletOnlyTd>
                      <Year>{dividend.year}</Year>
                    </TabletOnlyTd>
                    <Td>
                      <Year display="mobile">{dividend.year}</Year>
                      {dividend.interim}
                    </Td>
                    <Td>
                      <YearSpacer />
                      {dividend.final}
                    </Td>
                    <Td>
                      <YearSpacer />
                      {dividend.special}
                    </Td>
                    <Td>
                      <YearSpacer />
                      {dividend.total}
                    </Td>
                  </Tr>
                );
              }
            )}
          </Tbody>
        </DividendsTable>
      </TableContainer>
      {dividends.length > 5 && (
        <ButtonContainer>
          <ToggleButton isActive={isActive} setIsActive={setIsActive}>
            {translation[isActive ? "less" : "more"]}{" "}
          </ToggleButton>
        </ButtonContainer>
      )}
    </DividendsSection>
  );
};
export const dividendsName = "ContentfulPageSectionDividends";

export default Dividends;
